import React, { Component, ErrorInfo } from 'react';
import { Button } from '@/components/ui/button';
import { monitoring } from '@/lib/monitoring';

interface Props {
  children: React.ReactNode;
  resetKeys?: unknown[];
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorKey: number;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorKey: 0,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    // Encaminhar para Sentry com stack do React (ajuda no agrupamento e debugging).
    monitoring.captureException(error, {
      componentStack: errorInfo.componentStack,
      boundary: 'route-level',
    });
  }

  public componentDidUpdate(prevProps: Props) {
    if (!this.state.hasError) return;
    // Reseta quando resetKeys mudam (ex: pathname muda via navegação)
    const prevKeys = prevProps.resetKeys ?? [];
    const nextKeys = this.props.resetKeys ?? [];
    const changed = nextKeys.some((key, i) => key !== prevKeys[i]);
    if (changed) {
      this.setState({ hasError: false, error: null });
    }
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4">
          <p className="text-lg font-medium">Oops! Algo deu errado</p>
          <p className="text-sm text-muted-foreground">
            Ocorreu um erro inesperado. Tente recarregar a página.
          </p>
          <Button onClick={() => this.setState({ hasError: false, error: null })}>
            Tentar novamente
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
